/**
 * DocxEditorViewEntry — thin wrapper around DocxEditorView that owns the
 * code-split boundary and the error/suspense fallback UI. This is what
 * the MAIN_VIEWS registry points at; the real component (which pulls in
 * ~several MB of `@eigenpal/docx-js-editor` and its ProseMirror deps)
 * only loads when the user first opens a `.docx`.
 *
 * The error boundary catches two classes of failures the suspense
 * fallback can't handle by itself:
 *   1. Chunk-load errors (network drop mid-lazy-import, stale cached
 *      HTML pointing at a deleted asset after redeploy).
 *   2. Runtime exceptions from the docx parser on a malformed file.
 *
 * In both cases we show a minimal recovery UI with a Close button —
 * better than a white screen that takes out the whole main slot.
 */

import { Component, lazy, Suspense, type ReactNode } from 'react';
import type { AppState } from "@strands/agent-transport";

// Route-level code split. The `@eigenpal/docx-js-editor` library and all
// its ProseMirror deps live in a separate chunk that only ships to users
// who actually click a .docx file.
const DocxEditorView = lazy(() => import('./DocxEditorView'));

interface EntryProps {
  path?: string;
  state: AppState;
  sessionId: string | null;
  updateState: (key: string, value: unknown) => void;
  onClose: () => void;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class DocxErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error) {
    // Keep the console trail — Sentry/telemetry hook goes here later.
    console.error('[DocxEditorView] boundary caught:', error);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="docx-view docx-view--error">
          <div className="docx-view-error-msg">
            Не удалось открыть документ: {this.state.error.message}
          </div>
          <button
            type="button"
            className="docx-view-btn"
            onClick={this.props.onClose}
          >
            Закрыть
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function DocxEditorViewEntry(props: EntryProps) {
  // Missing path → registry was called wrong. Fail loud so the call site
  // gets fixed, rather than silently opening an empty view.
  if (!props.path) {
    return (
      <div className="docx-view docx-view--error">
        <div className="docx-view-error-msg">
          DocxEditorView requires a `path` prop.
        </div>
      </div>
    );
  }

  return (
    <DocxErrorBoundary onClose={props.onClose}>
      <Suspense
        fallback={<div className="docx-view-loading">Загрузка редактора…</div>}
      >
        <DocxEditorView
          path={props.path}
          state={props.state}
          sessionId={props.sessionId}
          updateState={props.updateState}
          onClose={props.onClose}
        />
      </Suspense>
    </DocxErrorBoundary>
  );
}
